import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { POS_TEMPLATES } from '../data/templates'
import type { FieldMapping } from '@retailsync/shared-types'
import {
  MapIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'

const TARGET_FIELDS: { key: string; label: string; type: string; required: boolean }[] = [
  { key: 'name', label: 'Product Name', type: 'string', required: true },
  { key: 'sku', label: 'SKU', type: 'string', required: true },
  { key: 'category', label: 'Category', type: 'string', required: true },
  { key: 'price', label: 'Price', type: 'currency', required: true },
  { key: 'cost', label: 'Cost', type: 'currency', required: false },
  { key: 'stock', label: 'Stock Quantity', type: 'number', required: false },
  { key: 'description', label: 'Description', type: 'string', required: false },
  { key: 'barcode', label: 'Barcode/UPC', type: 'string', required: false },
  { key: 'batch_number', label: 'Batch Number', type: 'string', required: false },
  { key: 'expiry_date', label: 'Expiry Date', type: 'date', required: false },
  { key: 'generic_name', label: 'Generic Name', type: 'string', required: false },
  { key: 'strength', label: 'Strength', type: 'string', required: false },
  { key: 'drug_classification', label: 'Drug Classification', type: 'string', required: false },
  { key: 'requires_prescription', label: 'Requires Prescription', type: 'boolean', required: false },
]

export function DataMapping() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewMapping, setShowNewMapping] = useState(false)

  const selectedTemplate = POS_TEMPLATES.find(t => t.id === selectedTemplateId)

  const filteredTemplates = POS_TEMPLATES.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Mapping</h1>
          <p className="text-gray-600 mt-1">
            Configure and manage field mappings between POS exports and RetailsyncGo entities
          </p>
        </div>
        <button
          onClick={() => setShowNewMapping(!showNewMapping)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Mapping
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 space-y-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Template Cards */}
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className={`cursor-pointer transition-all ${
                  selectedTemplateId === template.id
                    ? 'ring-2 ring-blue-500 bg-blue-50'
                    : 'hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedTemplateId(template.id)
                  setShowNewMapping(false)
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{template.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                          {template.format.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-400">
                          {template.defaultMappings.length} mappings
                        </span>
                      </div>
                    </div>
                    {selectedTemplateId === template.id && (
                      <CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8 text-gray-500 text-sm">
                No templates match your search.
              </div>
            )}
          </div>
        </div>

        {/* Mapping Details */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedTemplate.name}</CardTitle>
                    <CardDescription>{selectedTemplate.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <DocumentTextIcon className="h-4 w-4" />
                    <span>Format: {selectedTemplate.format.toUpperCase()}</span>
                    <span className="mx-1">•</span>
                    <span>Entity: {selectedTemplate.entity}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>
                      {selectedTemplate.defaultMappings.length} field mappings configured for{' '}
                      <strong>{selectedTemplate.entity}</strong>
                    </span>
                  </div>

                  {/* Mappings Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Source Field</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">
                            <MapIcon className="h-4 w-4 inline mr-1" />
                            Target Field
                          </th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Transform</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">Required</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedTemplate.defaultMappings.map((mapping: FieldMapping, idx: number) => {
                          const targetField = TARGET_FIELDS.find(f => f.key === mapping.targetField)
                          return (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-2.5 font-mono text-gray-700 text-xs">
                                {mapping.sourceField}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-gray-900">
                                    {targetField?.label || mapping.targetField}
                                  </span>
                                  <span className="text-xs text-gray-400">({mapping.targetField})</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  mapping.transform === 'currency'
                                    ? 'bg-green-100 text-green-700'
                                    : mapping.transform === 'number'
                                    ? 'bg-blue-100 text-blue-700'
                                    : mapping.transform === 'date'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {mapping.transform || 'string'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {mapping.required ? (
                                  <span className="text-red-500 font-bold">*</span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>

                  {/* Unmapped Target Fields */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped RetailsyncGo Fields</h4>
                    <div className="flex flex-wrap gap-2">
                      {TARGET_FIELDS.filter(f =>
                        !selectedTemplate.defaultMappings.some(m => m.targetField === f.key)
                      ).map(field => (
                        <span
                          key={field.key}
                          className="px-3 py-1 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-full text-xs"
                          title={field.required ? 'Required field' : 'Optional field'}
                        >
                          {field.label} {field.required && '*'}
                        </span>
                      ))}
                      {TARGET_FIELDS.filter(f =>
                        !selectedTemplate.defaultMappings.some(m => m.targetField === f.key)
                      ).length === 0 && (
                        <span className="text-xs text-green-600">All fields mapped ✓</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
              <MapIcon className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500">Select a template to view its field mappings</p>
              <p className="text-xs text-gray-400 mt-1">
                {POS_TEMPLATES.length} templates available
              </p>
            </div>
          )}

          {/* New Mapping Panel */}
          {showNewMapping && (
            <Card className="mt-4 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-base text-blue-900">Create New Mapping Template</CardTitle>
                <CardDescription className="text-blue-700">
                  Define custom field mappings for your POS system export format.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                    <input
                      type="text"
                      placeholder="My Custom POS"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="products">Products</option>
                      <option value="transactions">Transactions</option>
                      <option value="customers">Customers</option>
                      <option value="inventory">Inventory</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-3">
                  Full template builder coming soon. For now, use the Import Wizard to create mappings interactively.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}